import {GlobalCommands} from '@github-ui/ui-commands'
import {ArrowLeftIcon, XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {type RefObject, useEffect, useLayoutEffect, useRef} from 'react'

import {ArtifactCopyButton} from './ArtifactCopyButton'
import {ArtifactEditButton} from './ArtifactEditButton'
import {ArtifactPanelBody, type PanelArtifact} from './ArtifactPanelContent'
import {ArtifactsPanelPortalProvider} from './ArtifactsPanelPortalProvider'
import styles from './ArtifactsSidePanelEmbedded.module.css'
import {ArtifactsTabBar, type TabItem} from './ArtifactsTabBar'
import type {EditableArtifact} from './create-artifact-dialog-types'
import {useArtifactsPanelTopVar} from './use-app-header-bottom'

type ArtifactsSidePanelEmbeddedProps = {
  open: boolean
  onClose: () => void
  tabs: TabItem[]
  activeId: string | null
  activeArtifact: PanelArtifact | null
  onEdit?: (artifact: EditableArtifact, returnFocusRef: RefObject<HTMLElement | null>) => void
  onActivate: (id: string) => void
  onPromoteOverflowTab: (id: string) => void
  onCloseTab?: (id: string) => void
  returnFocusRef?: RefObject<HTMLElement | null>
  activeTabRef?: RefObject<HTMLButtonElement | null>
  docked?: boolean
  navigation?: 'back' | 'close'
}

export function ArtifactsSidePanelEmbedded({
  open,
  onClose,
  tabs,
  activeId,
  activeArtifact,
  onEdit,
  onActivate,
  onPromoteOverflowTab,
  onCloseTab,
  returnFocusRef,
  activeTabRef,
  docked = false,
  navigation = 'close',
}: ArtifactsSidePanelEmbeddedProps) {
  useArtifactsPanelTopVar(open && !docked)

  // Set a body-level marker while open so cross-module layout rules can react
  // via `body[data-artifacts-embedded-open]` (cheap attribute match) instead of
  // `body:has([data-artifacts-embedded-open])` (whole-tree :has() invalidation).
  useLayoutEffect(() => {
    if (docked || typeof document === 'undefined') return
    if (!open) return
    document.body.setAttribute('data-artifacts-embedded-open', 'true')
    return () => {
      document.body.removeAttribute('data-artifacts-embedded-open')
    }
  }, [docked, open])

  // The overlay variant returns focus via SidePanel; the embedded panel becomes
  // `inert` on close, so restore focus to the originating row here.
  const wasOpenRef = useRef(open)
  useEffect(() => {
    if (wasOpenRef.current && !open) {
      returnFocusRef?.current?.focus({preventScroll: true})
    }
    wasOpenRef.current = open
  }, [open, returnFocusRef])

  return (
    <aside
      className={clsx(styles.Panel, docked && styles.docked, !open && styles.closed)}
      aria-label="Artifacts"
      aria-hidden={!open}
      inert={!open || undefined}
      data-artifacts-embedded-open={!docked && open ? true : undefined}
      data-testid="issue-artifacts-panel"
    >
      {open && <GlobalCommands commands={{'github:cancel': onClose}} />}
      <ArtifactsPanelPortalProvider>
        <ArtifactsTabBar
          tabs={tabs}
          activeId={activeId}
          onActivate={onActivate}
          onPromoteOverflowTab={onPromoteOverflowTab}
          onCloseTab={onCloseTab}
          activeTabRef={activeTabRef}
          leadingAction={
            navigation === 'back' ? (
              <IconButton icon={ArrowLeftIcon} variant="invisible" aria-label="Back to issue" onClick={onClose} />
            ) : undefined
          }
          trailingActions={
            <>
              {onEdit && <ArtifactEditButton artifact={activeArtifact} onEdit={onEdit} />}
              <ArtifactCopyButton artifact={activeArtifact} />
              {navigation === 'close' && (
                <IconButton icon={XIcon} variant="invisible" aria-label="Close panel" onClick={onClose} />
              )}
            </>
          }
        />
        <div
          className={styles.Content}
          id={activeArtifact ? `artifact-panel-${activeArtifact.id}` : undefined}
          role={activeArtifact ? 'tabpanel' : undefined}
          aria-labelledby={activeArtifact ? `artifact-tab-${activeArtifact.id}` : undefined}
        >
          {activeArtifact && <ArtifactPanelBody artifact={activeArtifact} />}
        </div>
      </ArtifactsPanelPortalProvider>
    </aside>
  )
}
